import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { centsFromDecimal, formatCurrency } from '@eis/core';
import { bareEnum } from '../../policies/model/types';
import type { AvailableTransition, Lead, LeadStatusKind, LeadVersion } from './types';

/**
 * View models for the Leads surface (SDD-013 §8.1).
 *
 * # The status is the tenant's; the KIND is the platform's
 *
 * A lead's `status.code` and `status.name` come from the tenant's pinned lifecycle asset —
 * `offered`, `Angebot erstellt` — and the console never interprets them: it shows the name. What
 * it acts on is the KIND (OPEN / ACCEPTED / CONVERTED / CLOSED), which decides colour, which
 * actions are offered and whether Accept may run. Nothing here hard-codes a status code.
 *
 * # The word this file may not use
 *
 * "Version", in the configuration sense (SSOT I-6): the operations persona reads a lead's
 * configuration as a product name and a date, never as `v3`. A lead's own `version` — the head's
 * row version — is shown in the History tab as "Stand", exactly as a policy's business version is.
 */

export const kindOf = (lead: Lead | undefined): string => bareEnum(lead?.status?.kind, 'STATUS_KIND_');

/**
 * The colour a kind takes (SDD-013 §8.1.1): OPEN neutral, ACCEPTED informational, CONVERTED
 * success, CLOSED default — expressed through the theme's status tokens, never a hex value.
 * The label beside it is always the tenant's status NAME.
 */
export function kindToken(kind: string): string {
  switch (kind) {
    case 'ACCEPTED':
      return 'PUBLISHED';
    case 'CONVERTED':
      return 'ACTIVE';
    case 'CLOSED':
      return 'TERMINATED';
    default:
      return 'DRAFT';
  }
}

export const isMemberLead = (lead: Lead | undefined): boolean =>
  (lead?.draft?.parentPolicyCode ?? '') !== '';

/** Editable: OPEN and not locked (SDD-013 §6.2). Update and Refresh are offered only then. */
export const isEditable = (lead: Lead | undefined): boolean =>
  kindOf(lead) === 'OPEN' && lead?.status?.locked !== true;

export const hasDrafts = (lead: Lead | undefined): boolean =>
  (lead?.partnerDrafts ?? []).length > 0;

/** Registering resolves an identity, not the offer: allowed while the lead is not terminal. */
export const canRegister = (lead: Lead | undefined): boolean =>
  hasDrafts(lead) && kindOf(lead) !== 'CONVERTED' && kindOf(lead) !== 'CLOSED';

/** The transitions a USER may take from here; SYSTEM ones are shown, never offered (§5.5). */
export const userTransitions = (
  available: readonly AvailableTransition[] | undefined,
): AvailableTransition[] => (available ?? []).filter((t) => t.actor !== 'ACTOR_SYSTEM');

export type AcceptBlocker = 'converted' | 'notAccepted' | 'parentNotActive';

/**
 * Why Accept is disabled, in SDD-013 §7.2's order — the console states the reason instead of
 * hiding the button, so an operator learns what has to happen first.
 *
 * A remaining DRAFT no longer blocks (SDD-014 PS-5, prompt 20): the accept registers it in the
 * Partner service first, in its own write, and the dialog says so. "Partner registrieren" stays
 * as the explicit way to do it earlier.
 */
export function acceptBlocker(lead: Lead | undefined, parentStatus: string | undefined): AcceptBlocker | null {
  const kind = kindOf(lead);
  if (kind === 'CONVERTED') return 'converted';
  if (kind !== 'ACCEPTED') return 'notAccepted';
  if (isMemberLead(lead) && parentStatus !== undefined && parentStatus !== 'ACTIVE') return 'parentNotActive';
  return null;
}

/** A `Money` formatted for the reader; the raw string when it does not parse (never 0,00 €). */
export function moneyLabel(amount: InstanceApi.Money | undefined, locale: string): string {
  const value = amount?.amount ?? '';
  const currency = amount?.currency ?? '';
  if (value === '') return '—';
  const cents = centsFromDecimal(value);
  if (cents === null) return currency === '' ? value : `${value} ${currency}`;
  return formatCurrency(cents, currency === '' ? 'EUR' : currency, locale);
}

/** `tarifservice:fahrrad/2026/1.2` → `fahrrad`: the product the reader is told, without its coordinates. */
export function tarifNameOf(tarifVersionErn: string | undefined): string {
  const ern = tarifVersionErn ?? '';
  const afterColon = ern.slice(ern.indexOf(':') + 1);
  const slash = afterColon.indexOf('/');
  return slash === -1 ? afterColon : afterColon.slice(0, slash);
}

/**
 * The draft partner's name and type, for display — in either spelling a draft may carry: SDD-013's
 * `{typeCode, name}` (the golden member lead) or the Partner service's own create shape
 * `{partnerTypeCode, customFields}` (what the lead form writes since SDD-014 PS-8). The name of a
 * service-shaped draft is derived the way the service derives `displayName` (PROBE.md §6).
 */
export function draftPartnerLabel(draft: InstanceApi.PartnerDraft): { name: string; typeCode: string } {
  const partner = draft.partner ?? {};
  const name = partner.name;
  const typeCode = partner.typeCode;
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const custom = (partner.customFields ?? {}) as Record<string, unknown>;
  const basic = (custom.BasicInformation ?? {}) as Record<string, unknown>;
  const derived =
    text(basic.organizationName) !== ''
      ? text(basic.organizationName)
      : `${text(basic.firstName)} ${text(basic.lastName)}`.trim();
  return {
    name: typeof name === 'string' && name !== '' ? name : derived,
    typeCode: typeof typeCode === 'string' ? typeCode : text(partner.partnerTypeCode),
  };
}

/** A row of the list, flattened for the grid (SDD-013 §8.1.1). */
export interface LeadRow {
  readonly id: string;
  readonly code: string;
  readonly statusCode: string;
  readonly statusName: string;
  readonly kind: string;
  readonly tarif: string;
  /** The policyholder — a member's resolved through the master — or the first draft's name, marked. */
  readonly partner: string;
  readonly partnerIsDraft: boolean;
  readonly monthlyPremium: string;
  readonly currency: string;
  readonly effectiveFrom: string;
  readonly validUntil: string;
  readonly updatedAt: string;
  readonly parentPolicyCode: string;
  readonly policyCode: string;
  readonly version: string;
}

/**
 * The partner a row is about (SDD-013 §8.1.1): a DRAFT first — the person this offer is for is
 * not registered yet, and that is the row's news; else, for a member, the insured person (every
 * member shares the master's policyholder, so naming the bank on every row says nothing); else
 * the standalone lead's policyholder. Resolved names when the directory knows them, the id
 * otherwise — never a name invented here.
 */
function partnerOfRow(lead: Lead): { partner: string; partnerIsDraft: boolean } {
  const firstDraft = lead.partnerDrafts?.[0];
  if (firstDraft !== undefined) {
    return { partner: draftPartnerLabel(firstDraft).name, partnerIsDraft: true };
  }
  const draft = lead.draft;
  const wanted = isMemberLead(lead) ? draft?.insuredPerson?.partnerId : draft?.policyholder?.resolvedPartnerId;
  const resolved = draft?.partners?.find((partner) => partner.link?.partnerId === wanted);
  const name =
    resolved?.partnerNameResolved === true && (resolved.partnerName ?? '') !== ''
      ? (resolved.partnerName ?? '')
      : (wanted ?? '');
  return { partner: name, partnerIsDraft: false };
}

export function toLeadRows(leads: readonly Lead[] | undefined): LeadRow[] {
  return (leads ?? []).map((lead) => {
    const draft = lead.draft;
    const { partner, partnerIsDraft } = partnerOfRow(lead);
    return {
      id: lead.code ?? '',
      code: lead.code ?? '',
      statusCode: lead.status?.code ?? '',
      statusName: lead.status?.name ?? lead.status?.code ?? '',
      kind: kindOf(lead),
      tarif: tarifNameOf(draft?.configuration?.resolvedTarifVersionErn),
      partner,
      partnerIsDraft,
      monthlyPremium: draft?.premium?.monthly?.amount ?? '',
      currency: draft?.premium?.monthly?.currency ?? '',
      effectiveFrom: lead.effectiveFrom ?? '',
      validUntil: lead.validUntil ?? '',
      updatedAt: lead.updatedAt ?? '',
      parentPolicyCode: draft?.parentPolicyCode ?? '',
      policyCode: lead.policyCode ?? '',
      version: lead.version ?? '',
    };
  });
}

/** Whether an offer's validity has passed (the list marks it; the sweep closes it). */
export const isPastValidity = (validUntil: string, today: string): boolean =>
  validUntil !== '' && validUntil < today;

/** The kind counts the group tab's header states: "12 open · 3 approved · …". */
export function kindCounts(leads: readonly Lead[] | undefined): Record<LeadStatusKind, number> {
  const counts: Record<LeadStatusKind, number> = {
    STATUS_KIND_UNSPECIFIED: 0,
    STATUS_KIND_OPEN: 0,
    STATUS_KIND_ACCEPTED: 0,
    STATUS_KIND_CONVERTED: 0,
    STATUS_KIND_CLOSED: 0,
  };
  for (const lead of leads ?? []) {
    const kind = lead.status?.kind ?? 'STATUS_KIND_UNSPECIFIED';
    counts[kind] += 1;
  }
  return counts;
}

/** `{ bike: { bike_value: 3200 } }` → `{ 'bike.bike_value': '3200' }`: the payload as label/value rows. */
export function flattenData(data: Record<string, unknown> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (prefix: string, value: unknown): void => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
        walk(prefix === '' ? key : `${prefix}.${key}`, inner);
      }
      return;
    }
    if (Array.isArray(value)) out[prefix] = JSON.stringify(value);
    else if (typeof value === 'string') out[prefix] = value;
    else if (typeof value === 'number' || typeof value === 'boolean') out[prefix] = String(value);
    else out[prefix] = '';
  };
  for (const [key, value] of Object.entries(data ?? {})) walk(key, value);
  return out;
}

/**
 * The fields a version changed against the one before it (SDD-013 §8.1.3, the History tab's
 * diff marker): status, premium, every payload field, the parties, the drafts, the dates, the
 * configuration. Keys are what the sections use to mark a row.
 */
export function changedFields(previous: Lead | undefined, next: Lead | undefined): Set<string> {
  const changed = new Set<string>();
  if (previous === undefined || next === undefined) return changed;
  if (previous.status?.code !== next.status?.code) changed.add('status');
  if (previous.draft?.premium?.monthly?.amount !== next.draft?.premium?.monthly?.amount) changed.add('premium.monthly');
  if (previous.draft?.premium?.base?.amount !== next.draft?.premium?.base?.amount) changed.add('premium.base');
  if (previous.effectiveFrom !== next.effectiveFrom) changed.add('effectiveFrom');
  if (previous.validUntil !== next.validUntil) changed.add('validUntil');
  if (previous.policyCode !== next.policyCode) changed.add('policyCode');
  const pin = (lead: Lead): string =>
    `${lead.draft?.configuration?.resolvedTarifVersionErn ?? ''}|${lead.draft?.configuration?.groupTarifVersionErn ?? ''}`;
  if (pin(previous) !== pin(next)) changed.add('configuration');
  const before = flattenData(previous.draft?.data);
  const after = flattenData(next.draft?.data);
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (before[key] !== after[key]) changed.add(`data.${key}`);
  }
  const partnersKey = (lead: Lead): string =>
    (lead.draft?.partners ?? [])
      .map((partner) => `${partner.link?.roleErn ?? ''}|${partner.link?.partnerId ?? ''}`)
      .sort()
      .join(',');
  if (partnersKey(previous) !== partnersKey(next)) changed.add('partners');
  const draftsKey = (lead: Lead): string =>
    JSON.stringify((lead.partnerDrafts ?? []).map((draft) => [draft.roleErn, draft.partner]));
  if (draftsKey(previous) !== draftsKey(next)) changed.add('partnerDrafts');
  return changed;
}

/** The versions newest first — how the History tab lists them. */
export const newestFirst = (versions: readonly LeadVersion[] | undefined): LeadVersion[] =>
  [...(versions ?? [])].sort((a, b) => Number(b.version ?? '0') - Number(a.version ?? '0'));

/** The version recorded right before `version`, for the diff. */
export function previousVersion(
  versions: readonly LeadVersion[] | undefined,
  version: LeadVersion,
): LeadVersion | undefined {
  const target = Number(version.version ?? '0') - 1;
  return (versions ?? []).find((candidate) => Number(candidate.version ?? '0') === target);
}
